import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Field, FieldProps, FormikProps } from 'formik';
import Select from 'antd/es/select';
import Checkbox from 'antd/es/checkbox';
import Tooltip from 'antd/es/tooltip';

import { State } from '@common/react/components/Forms/State/State';
import loadable from '@common/react/loadable/loadableSettings';
import Autocomplete from '@common/react/components/Forms/Autocomplete/Autocomplete';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { parseQuery } from '@common/typescript/utils/url';
import { ZipCode } from '@common/typescript/objects/ZipCode';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { Gender, GenderNames } from '@commonTuna/react/objects/Enums';

import { RegistrationUser, User } from '@app/objects/User';
import { Location } from '@app/objects/Location';
import DatePickerLoader from '@app/components/UI/DatePickerLoader';

const DatePickerReact: any = loadable(
	() => import(/* webpackChunkName: "CustomDatePickerReact" */ '@common/react/components/UI/DatePickerReact/AdaptiveDatepicker'),
	{ fallback: <DatePickerLoader placeholder="Date..." /> },
);

type FormValues = RegistrationUser | User;

interface ComponentProps {
	formikBag: FormikProps<FormValues>;
	showAddress: boolean;
}

const SelectOption = Select.Option;

const PASSWORD_REQUIREMENTS_MESSAGE = 'Password should be at least 8 characters long';

const DemographicInfoRegistration: React.FC<ComponentProps> = ({ formikBag, showAddress }) => {
	const [isInvite, setIsInvite] = React.useState(false);
	const [emailConfirmed, setEmailConfirmed] = React.useState(false);
	const [phoneNumberConfirmed, setPhoneNumberConfirmed] = React.useState(false);

	const location = useLocation();
	const { getUser } = useApplicationContext();
	const user = getUser<User>();

	const onUserLoad = (user: User) => {
		formikBag.setValues({
			...formikBag.values,
			id: user.id,
			email: user.email,
			firstName: user.firstName,
			lastName: user.lastName,
			birthDate: user.birthDate,
			phoneNumber: user.phoneNumber,
			patientLocations: user.patientLocations,
			insurances: user.insurances,
			drivingLicenses: user.drivingLicenses,
		});

		setEmailConfirmed(user.emailConfirmed);
		setPhoneNumberConfirmed(user.phoneNumberConfirmed);
	};

	React.useEffect(() => {
		const query = parseQuery(location.search);
		if (user && user.id === +query.userId && user.completedRegistration === false) {
			onUserLoad(user);
		} else if (query.userId && query.token) {
			// getUserByInviteToken(+query.userId, query.token);
		}
	}, []);

	/* const getUserByInviteToken = (userId: number, token: string) => {
		request<User>('getUserByInviteToken', { userId, token })
			.then((res) => {
				setIsInvite(true);
				onUserLoad(res);
			})
			.catch((err) => {
				console.log(err);
			});
	}; */

	const NameRender = ({ form, field }: FieldProps) =>
		<input
			className="form-control"
			type="text"
			id={field.name}
			maxLength={50}
			value={field.value}
			onChange={(value) => form.setFieldValue(field.name, value.currentTarget.value.replace(/[^a-z'-]/gi, ''))}
		/>;

	const renderPatientInfo = (formikBag: FormikProps<FormValues>) => {
		return <>
			<div className="row">
				<FormikField
					fieldName="firstName"
					title="First Name*"
					containerClassName="form-group col-sm-3"
					render={NameRender}
					withValidIndicator
				/>
				<FormikField
					fieldName="lastName"
					title="Last Name*"
					containerClassName="form-group col-sm-3"
					render={NameRender}
					withValidIndicator
				/>
				<FormikField
					fieldName="gender"
					title="Gender*"
					containerClassName="form-group col-sm-3"
					withValidIndicator
					customValidCondition={(errors) => !errors}
					render={({ field, form }: FieldProps<FormValues>) =>
						<Select
							id={field.name}
							value={field.value?.toString()}
							onChange={(value) => form.setFieldValue(field.name, +value)}
						>
							{Object.keys(GenderNames).filter((key) => +key !== Gender.None).map((key) =>
								<SelectOption value={key} key={key}>{GenderNames[key]}</SelectOption>)}
						</Select>
					}
				/>
				<FormikField
					fieldName="birthDate"
					title="Birth Date*"
					withValidIndicator
					containerClassName="form-group col-sm-3"
					render={({ field, form }) => (
						<DatePickerReact
							placeholder="Select date"
							value={field.value}
							onChange={(date) => form.setFieldValue(field.name, date)}
							datePickerProps={{
								id: 'birthDate',
								maxDate: new Date(+new Date() - 18 * 365 * 24 * 1000 * 3600),
								showMonthDropdown: true,
								showYearDropdown: true,
								dropdownMode: 'select',
							}}
							utc
						/>
					)}
				/>
			</div>
			{showAddress ? <div className="form-grey-block clearfix">
				<div className="row">
					<div className="col-sm-12">
						<FormikField
							fieldName="zip"
							title="Zip*"
							withValidIndicator
							containerClassName="form-group col-sm-2"
							render={(fieldProps) => (
								<Autocomplete<ZipCode>
									type="zipCodeListRemote"
									renderTitle="zip"
									loadOnFocus
									onSelect={(value, option) => {
										formikBag.setValues({
											...formikBag.values,
											zip: option.props.item.zip,
											city: option.props.item.city,
											region: option.props.item.stateName,
										});
									}}
									onChange={(value = '') => formikBag.setFieldValue(fieldProps.field.name, value)}
									value={fieldProps.field.value}
								/>
							)}
						/>
						<FormikField
							fieldName="city"
							title="City*"
							withValidIndicator
							containerClassName="form-group col-sm-2"
						/>
						<FormikField
							fieldName="region"
							title="State*"
							withValidIndicator
							containerClassName="form-group col-sm-3"
							render={({ field, form }) => (
								<State antdProps={{ id: 'region' }} onChange={(state) => form.setFieldValue(field.name, state)} value={field.value} />
							)}
						/>
						<FormikField
							fieldName="address"
							title="Address"
							withValidIndicator
							containerClassName="form-group col-sm-5"
						/>
					</div>
				</div>
			</div> : null}
			<div className="row">
				<FormikField
					fieldName="email"
					title="Email*"
					withValidIndicator
					containerClassName="form-group col-sm-3"
					inputProps={{ disabled: emailConfirmed }}
				/>
				<FormikField
					containerClassName="form-group col-sm-3"
					fieldName="phoneNumber"
					title="Cell Phone*"
					withValidIndicator
					render={(fieldProps) => <FormikPhoneControl
						fieldProps={fieldProps}
						placeholder="+1 (___) ___-____"
						disabled={phoneNumberConfirmed}
					/>}
				/>
				<FormikField
					fieldName="patientLocations"
					title="Clinic"
					withValidIndicator
					customValidCondition={(errors, _, value) => !errors && value?.length > 0}
					containerClassName="form-group col-sm-6"
					render={({ field, form }: FieldProps<FormValues>) => (
						<Autocomplete<Location>
							type="locationList"
							renderTitle={(item: Location) => `${item?.company ? `${item.company.name} - ` : ''}${item.nameEn}`}
							onSelect={(value, option) => {
								formikBag.setFieldValue(field.name, [{
									locationId: +value,
									locationName: `${option.props.item?.company
										? `${option.props.item.company.name} - `
										: ''}${option.props.item.nameEn}`,
								}]);
							}}
							onChange={(value = '') => {
								if (value === '') {
									formikBag.setFieldValue(field.name, []);
								}
							}}
							antdProps={{
								id: 'patientLocations',
								popupClassName: 'long-dropdown',
							}}
							value={formikBag.values.patientLocations[0] ? formikBag.values.patientLocations[0].locationName : ''}
							loadOnFocus
							disabled={isInvite}
						/>)
					}
				/>
			</div>
			<div className="row">
				<FormikField
					fieldName="password"
					title="Password*"
					withValidIndicator
					inputProps={{ type: 'password' }}
					render={({ form, field }: FieldProps, inputProps?: React.HTMLProps<HTMLInputElement>) => (
						<Tooltip placement="topLeft" trigger="focus" title={PASSWORD_REQUIREMENTS_MESSAGE}>
							<input
								className="form-control"
								type="text"
								id={field.name}
								{...field}
								{...inputProps}
								autoComplete="one-time-code"
							/>
						</Tooltip>
					)}
				/>
				<FormikField withValidIndicator fieldName="repeatPassword" title="Repeat Password*" inputProps={{ type: 'password' }} />
			</div>
		</>;
	};

	return (
		<>
			<div className="form-box">
				<div className="form-box__body">
					{renderPatientInfo(formikBag)}
				</div>
			</div>
			<div className="form-box">
				<div className="form-box__body">
					<div className="row">
						<div className="form-group col-sm-12">
							<label>
								Do we have your permission to contact you for appointment reminders|promotions and specials via:
							</label>
							<div>
								<Field name="allowSms">{({ field }) => <Checkbox {...field} checked={field.value}> Text </Checkbox>}</Field>
								<Field name="allowEmail">{({ field }) => <Checkbox {...field} checked={field.value}> Email </Checkbox>}</Field>
								<Field name="allowPush">{({ field }) => <Checkbox {...field} checked={field.value}> Push </Checkbox>}</Field>
								<Field name="allowCalls">{({ field }) => <Checkbox {...field} checked={field.value}> Calls </Checkbox>}</Field>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="form-group col-sm-12">
							By clicking “Create Account” I agree to the&nbsp;
							<a className="privacy-policy-text" href="/privacy-policy" target="_blank">Privacy Policy</a>
							<div className="link pull-right">
								<Link to="/login">Already have an account?</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default DemographicInfoRegistration;
